import type * as React from 'react';

import type { Organization } from '@gitbook/api-client';

import { useUpgrade } from '../../../state';
import { AISearchAnswer, type AISearchQueryState } from '../../AISearch';
import { useRegisterRecentSearch, useSearchQueryAnalyticsEvent } from '../../Search';
import { AppSearchBlankslate } from './AppSearchBlankslate';
import { AppSearchResults } from './AppSearchResults';

/**
 * Body of the search/ask-gitbook dialog.
 */
export function AppSearchBody(props: {
    organization: Organization | null;
    query: string;
    aiQuery: AISearchQueryState;
    onUpdateQuery: (query: string) => void;
    onSubmitAIQuery: (newQuery: string, trigger?: 'click_ask_gitbook' | 'press_enter') => void;
    usingAI: boolean;
}): React.ReactElement {
    const { organization, query, aiQuery, onUpdateQuery, onSubmitAIQuery, usingAI } = props;

    useRegisterRecentSearch(query);
    useSearchQueryAnalyticsEvent(query, { organization });

    // Once an answer is loaded and displayed, we clear the query
    // as the question is shown in the body.
    const onAnswerLoaded = () => {
        onUpdateQuery('');
    };

    if (organization && usingAI) {
        return (
            <AISearchAnswer
                target={{ organization: organization.id }}
                query={aiQuery}
                onAnswerLoaded={onAnswerLoaded}
                onSubmitQuery={onSubmitAIQuery}
            />
        );
    }

    if (!query) {
        return (
            <AppSearchBlankslate
                organization={organization}
                onUpdateQuery={onUpdateQuery}
                onSubmitAIQuery={onSubmitAIQuery}
            />
        );
    }

    if (organization) {
        // we need the organization to determine whether AI is enabled so only render the AI version
        // if we have an organization
        return (
            <AppSearchResultsWithAI
                query={query}
                onSubmitAIQuery={onSubmitAIQuery}
                organization={organization}
            />
        );
    }

    return <AppSearchResults query={query} onSubmitAIQuery={onSubmitAIQuery} organization={null} />;
}

function AppSearchResultsWithAI({
    query,
    onSubmitAIQuery,
    organization,
}: {
    query: string;
    onSubmitAIQuery: (newQuery: string, trigger?: 'click_ask_gitbook' | 'press_enter') => void;
    organization: Organization;
}) {
    const needsUpgradeForAI = useUpgrade('ai-lens');

    return (
        <AppSearchResults
            query={query}
            onSubmitAIQuery={onSubmitAIQuery}
            hasAI={!needsUpgradeForAI && organization.ai}
            organization={organization.id}
        />
    );
}
